import React, { FC, useState } from 'react'

import { pricePresenter } from '@smarty-nx/code-utils'
import { getPlansSlugs, planCollectionNames } from '@smarty-nx/contentful'
import { colors, Typography } from '@smarty-nx/ds'
import Select, {
  components,
  GroupHeadingProps,
  MenuListProps,
} from 'react-select'

import { content } from './select-group-plan-dropdown.content'
import classes from './select-group-plan-dropdown.module.scss'
import { useGroupPlans } from '../../../hooks'

export type GroupPlanOption = {
  name: string
  originalPrice: number
  finalPrice: number
  color: string
}

export enum PlanType {
  Owner = 'owner',
  Member = 'member',
}

type SelectGroupPlanDropdownProps = {
  type: PlanType
  index?: number
  onSelect(value: GroupPlanOption, type: PlanType, index?: number): void
}

const VOICE_PLAN_SLUGS = getPlansSlugs(planCollectionNames.VOICE_PLANS)
const DATA_ONLY_PLAN_SLUGS = getPlansSlugs(planCollectionNames.DATA_ONLY_PLANS)
const MONEY_BACK_PLAN_SLUGS = getPlansSlugs(
  planCollectionNames.MONEY_BACK_PLANS
)

const GroupHeading = (props: GroupHeadingProps) => {
  return (
    <div
      className={classes.collapseGroupHeading}
      onClick={() => {
        const groupHeading = document.querySelector(
          `#${props.id}`
        ) as HTMLElement
        const groupHeadingParentNode =
          groupHeading?.parentElement?.parentElement
        groupHeadingParentNode?.classList.toggle(classes.collapsedGroup)
      }}
    >
      <components.GroupHeading {...props} />
    </div>
  )
}

const MenuList = (props: MenuListProps) => {
  const newProps = {
    ...props,
    children: Array.isArray(props.children)
      ? props.children.map((c) => {
          return {
            ...c,
            props: { ...c.props, className: classes.collapsedGroup },
          }
        })
      : props.children,
  }
  return <components.MenuList {...newProps} />
}

export const SelectGroupPlanDropdown: FC<SelectGroupPlanDropdownProps> = ({
  type,
  index,
  onSelect,
}) => {
  const [selectedColor, setSelectedColor] = useState<string>('UI/White')

  const { list: voicePlans } = useGroupPlans(VOICE_PLAN_SLUGS)
  const { list: dataOnlyPlans } = useGroupPlans(DATA_ONLY_PLAN_SLUGS)
  const { list: moneyBackPlans } = useGroupPlans(MONEY_BACK_PLAN_SLUGS)

  const voicePlansOptions: GroupPlanOption[] = voicePlans.map((plan) => ({
    name: plan.priceData.name,
    originalPrice: plan.priceData.originalPrice.value,
    finalPrice: plan.priceData.finalPrice.value,
    color: plan.priceData.style.planColor,
  }))

  const dataPlansOptions: GroupPlanOption[] = dataOnlyPlans.map((plan) => ({
    name: plan.priceData.name,
    originalPrice: plan.priceData.originalPrice.value,
    finalPrice: plan.priceData.finalPrice.value,
    color: plan.priceData.style.planColor,
  }))

  const discountPlansOptions: GroupPlanOption[] = moneyBackPlans.map(
    (plan) => ({
      name: plan.priceData.name,
      originalPrice: plan.priceData.originalPrice.value,
      finalPrice: plan.priceData.finalPrice.value,
      color: plan.priceData.style.planColor,
    })
  )

  const groupedOptions = [
    {
      title: content.voicePlansCategoryTitle,
      description: content.voicePlansCategoryDescription,
      options: voicePlansOptions,
    },
    {
      title: content.dataOnlyPlansCategoryTitle,
      description: content.dataOnlyPlansCategoryDescription,
      options: dataPlansOptions,
    },
    {
      title: content.dataDiscountPlansCategoryTitle,
      description: content.dataDiscountPlansCategoryDescription,
      options: discountPlansOptions,
    },
  ]

  const onPlanChange = (value: GroupPlanOption) => {
    setSelectedColor(value.color)
    onSelect(value, type, index)
  }

  const formatGroupLabel = (data) => (
    <div
      className={classes.groupPlanCategoryWrapper}
      data-testid="group-plan-category"
    >
      <Typography
        component="p"
        variant="Kollektif/TinyEmphasis"
        data-testid="group-plan-category-title"
      >
        {data.title}
      </Typography>
      <Typography
        component="p"
        variant="Kollektif/Tiny"
        data-testid="group-plan-category-description"
      >
        {data.description}
      </Typography>
    </div>
  )

  const formatOptionLabel = (data) => (
    <div
      className={classes.groupPlanOptionWrapper}
      style={{ backgroundColor: colors[data.color] }}
      data-testid="group-plan-option"
    >
      <Typography
        component="p"
        variant="Kollektif/TinyEmphasis"
        data-testid="group-plan-option-name"
      >
        {data.name}
      </Typography>
      &nbsp;
      <Typography
        component="p"
        variant="Kollektif/TinyEmphasis"
        data-testid="group-plan-option-price"
      >
        {pricePresenter(data.originalPrice)}
      </Typography>
    </div>
  )

  const customStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: colors[selectedColor],
      borderColor: colors['Brand/PrimaryBlack'],
      border: '1px solid',
      boxShadow: 'none',
      '&:hover': {
        borderColor: colors['Brand/PrimaryBlack'],
      },
      color: colors['Brand/PrimaryBlack'],
      fontFamily: 'Kollektif, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: '20px',
      minHeight: '32px',
      height: '32px',
      padding: 0,
      margin: 0,
      textAlign: 'left',
      transition: 0,
      width: '160px',
      cursor: 'pointer',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    valueContainer: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: colors['Brand/PrimaryBlack'],
      fontFamily: 'Kollektif, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '0.8rem',
      lineHeight: '20px',
      margin: 0,
      padding: '1px 6px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: colors['Brand/PrimaryBlack'],
      margin: 0,
      padding: '0 8px 0 0',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: colors['UI/White'],
      borderColor: colors['Brand/PrimaryBlack'],
      border: '1px solid',
      boxShadow: 'none',
      textAlign: 'left',
      top: '28px',
      right: 0,
      padding: 0,
      width: '220px',
      cursor: 'pointer',
    }),
    option: (base, { isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? colors['UI/White'] : 'inherit',
      '&:hover': {
        backgroundColor: isSelected ? colors['UI/White'] : 'inherit',
      },
      padding: '1px 6px',
      cursor: 'pointer',
    }),
    groupHeading: (base) => ({
      ...base,
      backgroundColor: colors['UI/White'],
      margin: 0,
      padding: '0 0 0 8px',
      color: colors['Brand/PrimaryBlack'],
      cursor: 'pointer',
    }),
  }

  const testId =
    index !== undefined
      ? `select-group-plan-dropdown-member-${(index + 1).toString()}`
      : 'select-group-plan-dropdown-owner'

  return (
    <div className={classes.groupPlanDropdownWrapper} data-testid={testId}>
      <Select
        options={groupedOptions}
        blurInputOnSelect={false}
        closeMenuOnSelect={true}
        isSearchable={false}
        placeholder={content.placeholder}
        onChange={(value) => onPlanChange(value)}
        styles={customStyles}
        formatGroupLabel={formatGroupLabel}
        formatOptionLabel={formatOptionLabel}
        components={{
          GroupHeading,
          MenuList,
          IndicatorSeparator: () => null,
        }}
      />
    </div>
  )
}

export default SelectGroupPlanDropdown
